import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import { Alert, AlertIcon, Box, Button, FormControl, FormLabel, Heading, Input, Spinner, Text, Tooltip, VStack } from '@chakra-ui/react';
import { CompleteEmailLinkSignupInput, EmailLoginLinkResult, Mutation, MutationAuthenticateEmailLoginLinkArgs, MutationCompleteEmailLinkSignupArgs, MutationRequestEmailSignupPhoneCodeArgs, MutationResumeEmailLinkSignupArgs } from '../../generated/graphql';
import { AUTHENTICATE_EMAIL_LOGIN_LINK, COMPLETE_EMAIL_LINK_SIGNUP, REQUEST_EMAIL_SIGNUP_PHONE_CODE, GET_MY_CLIENT, RESUME_EMAIL_LINK_SIGNUP } from './queries';
import { clearEmailLinkCredential, getEmailLinkCredential } from './utils/emailLinkCredential';
import { useAuth } from '../../contexts/AuthContext';

/**
 * The browser that opens an email sign-in link (or the page that typed the email's code, via
 * `initialResult`). It signs in and lands on the page that asked. It never approves another
 * browser (2026-09-11): the page that asked is signed in by typing the four-digit code from the
 * same email there. A pre-existing session wins and is never swapped.
 */
/**
 * Where "Continue" goes when the browser is ALREADY signed in (3 Oct 2026, One Group: a tag's email link
 * said "You're already signed in", and Continue went to the home page instead of the deal). The link's
 * own `?next=` page, when it is a path on THIS site; otherwise the default. Same rule as the exchange's
 * destination check: same origin, starts with "/", never "//" (a protocol-relative URL to another site).
 */
export function alreadySignedInDestination(defaultPath: string, search: string = window.location.search): string {
  const next = new URLSearchParams(search).get('next');
  if (!next || !next.startsWith('/') || next.startsWith('//') || next.startsWith('/\\')) return defaultPath;
  try {
    const url = new URL(next, window.location.origin);
    return url.origin === window.location.origin ? `${url.pathname}${url.search}${url.hash}` : defaultPath;
  } catch { return defaultPath; }
}

export function EmailLinkCallback({ defaultPath = '/', initialResult }: { defaultPath?: string; initialResult?: EmailLoginLinkResult }) {
  const apollo = useApolloClient();
  const { refreshAuth } = useAuth();
  const destination = useRef(defaultPath);
  const [result, setResult] = useState<EmailLoginLinkResult>();
  const [error, setError] = useState('');
  const [phoneSent, setPhoneSent] = useState(false);
  const [phoneRetryAt, setPhoneRetryAt] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [sendPhone, { loading: sendingPhone }] = useMutation<Pick<Mutation, 'requestEmailSignupPhoneCode'>, MutationRequestEmailSignupPhoneCodeArgs>(REQUEST_EMAIL_SIGNUP_PHONE_CODE);
  useEffect(() => { const timer = window.setInterval(() => setNow(Date.now()), 1000); return () => window.clearInterval(timer); }, []);
  const [finishing, setFinishing] = useState(false);
  const [values, setValues] = useState<Omit<CompleteEmailLinkSignupInput, 'proof'>>({});
  const [complete, { loading }] = useMutation<Pick<Mutation, 'completeEmailLinkSignup'>, MutationCompleteEmailLinkSignupArgs>(COMPLETE_EMAIL_LINK_SIGNUP);
  const exchange = useRef<Promise<EmailLoginLinkResult>>();
  const handled = useRef(false);
  const currentSession = useRef(!!localStorage.getItem('auth_token'));
  const accept = useRef(async (_value: EmailLoginLinkResult) => {});
  const continueHere = () => { clearEmailLinkCredential(); window.location.replace(destination.current); };
  accept.current = async value => {
    if (handled.current) return;
    if (!value.auth?.token) { setResult(value); return; }
    handled.current = true;
    setFinishing(true);
    setResult(undefined);
    if (!localStorage.getItem('auth_token')) localStorage.setItem('auth_token', value.auth.token);
    try {
      await apollo.clearStore();
      await apollo.query({ query: GET_MY_CLIENT, fetchPolicy: 'network-only' });
      refreshAuth();
      // A reload boots the app at the recovered destination with no stale user data.
      const path = value.returnPath;
      const url = new URL(path, window.location.origin);
      destination.current = url.origin === window.location.origin && path.startsWith('/') && !path.startsWith('//') ? path : defaultPath;
      continueHere();
    } catch {
      setError('You are signed in, but your profile could not be loaded. Retry to continue.');
      setFinishing(false);
    }
  };
  useEffect(() => {
    if (currentSession.current) return;
    if (initialResult) { void accept.current(initialResult); return; }
    if (!exchange.current) {
      const token = getEmailLinkCredential();
      // A module email may name the page to return to; the server validates it against the tenant origin and falls back to the stored returnPath.
      const nextPath = new URLSearchParams(window.location.search).get('next') || undefined;
      if (!token) { setError('This sign-in link is missing. Open the link in your email again or request a new one.'); return; }
      exchange.current = apollo.mutate<Pick<Mutation, 'authenticateEmailLoginLink'>, MutationAuthenticateEmailLoginLinkArgs>({ mutation: AUTHENTICATE_EMAIL_LOGIN_LINK, variables: { token, next: nextPath }, fetchPolicy: 'no-cache' }).then(({ data }) => {
        if (!data?.authenticateEmailLoginLink) throw new Error('Sign-in did not complete. Please try again.');
        return data.authenticateEmailLoginLink;
      });
    }
    let mounted = true;
    exchange.current.then(value => { if (mounted) void accept.current(value); }).catch((err: Error) => { if (mounted) setError(err.message); });
    return () => { mounted = false; };
  }, [apollo, initialResult]);
  useEffect(() => {
    if (!result?.proof || loading || finishing || handled.current) return;
    let stopped = false;
    let timer: number;
    const proof = result.proof;
    const until = Date.now() + 15 * 60 * 1000;
    async function resume() {
      if (stopped || handled.current || Date.now() > until) return;
      if (localStorage.getItem('auth_token')) { window.location.replace(result?.returnPath || defaultPath); return; }
      try {
        const { data } = await apollo.mutate<Pick<Mutation, 'resumeEmailLinkSignup'>, MutationResumeEmailLinkSignupArgs>({ mutation: RESUME_EMAIL_LINK_SIGNUP, variables: { proof } });
        if (!stopped && data?.resumeEmailLinkSignup?.auth) { await accept.current(data.resumeEmailLinkSignup); return; }
      } catch { /* The visible form remains available for explicit completion. */ }
      if (!stopped) timer = window.setTimeout(resume, 3000);
    }
    timer = window.setTimeout(resume, 3000);
    return () => { stopped = true; window.clearTimeout(timer); };
  }, [apollo, result, loading, finishing, defaultPath]);
  async function submit(event: FormEvent) {
    event.preventDefault();
    setError('');
    try {
      const { data } = await complete({ variables: { input: { ...values, phoneCode: values.phoneCode || undefined, proof: result!.proof! } } });
      if (data?.completeEmailLinkSignup) await accept.current(data.completeEmailLinkSignup);
    } catch (err) { setError(err instanceof Error ? err.message : 'Signup could not be completed. Please try again.'); }
  }
  const fields = ['fName', 'lName', 'phoneNumber'] as const;
  const singleName = result?.profileFields?.lName === 'hidden';
  return <Box minH="100dvh" display="flex" alignItems="center" p={6}><VStack as="form" onSubmit={submit} maxW="430px" w="full" mx="auto" spacing={6} align="stretch">
    <Heading as="h1" textAlign="center">{currentSession.current ? 'You’re already signed in' : result?.proof ? singleName && result.missingFields.includes('fName') ? 'Your Name, Please' : result.missingFields.length ? 'Complete your profile' : 'Ready to get started' : error ? 'Sign-in needs attention' : 'Signing you in…'}</Heading>
    {currentSession.current ? <><Text>Continue with your current account. To use a different account, sign out first and reopen the email.</Text><Button title="Continue to the page this email was for, with your current account" onClick={() => { clearEmailLinkCredential(); window.location.replace(alreadySignedInDestination(defaultPath)); }}>Continue</Button></> : <>
      {error && <Alert status="error"><AlertIcon />{error}</Alert>}
      {!error && !result?.proof && <Spinner alignSelf="center" label="Signing you in" />}
      {result?.proof && !finishing && <>
        <Text textAlign="center">{result.missingFields.length ? 'A few details to finish getting you set up. You only need to do this once.' : 'Continue to finish setting up your access.'}</Text>
        {fields.filter(field => result.profileFields?.[field] !== 'hidden' && (result.missingFields.includes(field) || result.profileFields?.[field] === 'optional')).map(field => <FormControl key={field} isRequired={result.profileFields?.[field] === 'required'}>
          <FormLabel htmlFor={`profile-${field}`}>{field === 'fName' ? singleName ? 'Your name' : 'First name' : field === 'lName' ? 'Last name' : 'Phone / mobile number'}</FormLabel>
          <Input id={`profile-${field}`} type={field === 'phoneNumber' ? 'tel' : 'text'} autoComplete={field === 'phoneNumber' ? 'tel' : field === 'fName' ? singleName ? 'name' : 'given-name' : 'family-name'} value={values[field] || ''} maxLength={field === 'phoneNumber' ? 32 : 120} onChange={event => { setValues({ ...values, [field]: event.target.value, ...(field === 'phoneNumber' ? { phoneCode: '' } : {}) }); if (field === 'phoneNumber') setPhoneSent(false); }} />
          {field === 'phoneNumber' && <>
            <Text fontSize="sm">Include your country code. Verify this number before saving it.</Text>
            <Tooltip label="Send a verification code to this phone number; no account is created by sending it.">
              <Button mt={3} isLoading={sendingPhone} isDisabled={!values.phoneNumber || phoneRetryAt > now} onClick={async () => {
                try {
                  const response = await sendPhone({ variables: { proof: result.proof!, phoneNumber: values.phoneNumber! } });
                  if (response.data?.requestEmailSignupPhoneCode.accepted) { setPhoneSent(true); setPhoneRetryAt(Date.now() + response.data.requestEmailSignupPhoneCode.retryAfterSeconds * 1000); }
                } catch (err) { setError(err instanceof Error ? err.message : 'Could not request the phone code.'); }
              }}>{phoneRetryAt > now ? `Resend in ${Math.ceil((phoneRetryAt - now) / 1000)}s` : 'Send phone verification code'}</Button>
            </Tooltip>
            {phoneSent && <Box mt={3}>
              <FormLabel htmlFor="signup-phone-code">Phone verification code</FormLabel>
              <Input id="signup-phone-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={values.phoneCode || ''} onChange={event => setValues({ ...values, phoneCode: event.target.value })} />
              <Text fontSize="sm">If this number can be added, a six-digit code will arrive by SMS.</Text>
            </Box>}
          </>}
        </FormControl>)}
        <Tooltip label="Save these details and finish creating your account."><Button type="submit" colorScheme="green" isLoading={loading} loadingText="Completing signup…">Continue</Button></Tooltip>
      </>}
      {error && <Button title="Return to sign-in to request a fresh link or retry loading your signed-in account" onClick={() => window.location.replace(localStorage.getItem('auth_token') ? defaultPath : '/login')}>Try again</Button>}
    </>}
  </VStack></Box>;
}
